/**
 * Consent management for analytics
 * GDPR/CCPA compliant consent handling
 */

import type { ConsentPreferences } from './types';

const CONSENT_COOKIE_NAME = 'bb-analytics-consent';
const CONSENT_EXPIRY_DAYS = 365;

/**
 * Get current consent preferences
 */
export function getConsentPreferences(): ConsentPreferences | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const cookies = document.cookie.split('; ');
    const consentCookie = cookies.find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));
    
    // Debug logging only when explicitly enabled
    if (process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true') {
      console.log('[Consent] All cookies:', cookies);
      console.log('[Consent] Looking for:', CONSENT_COOKIE_NAME);
      console.log('[Consent] Found cookie:', consentCookie);
    }
    
    if (!consentCookie) return null;
    
    const cookieValue = consentCookie.split('=')[1];
    const preferences = JSON.parse(decodeURIComponent(cookieValue));
    
    if (process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true') {
      console.log('[Consent] Parsed preferences:', preferences);
    }
    
    return preferences;
  } catch (error) {
    if (process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true') {
      console.error('[Consent] Error parsing cookie:', error);
    }
    return null;
  }
}

/**
 * Save consent preferences
 */
export function saveConsentPreferences(preferences: Omit<ConsentPreferences, 'timestamp'>): void {
  if (typeof window === 'undefined') return;
  
  const fullPreferences: ConsentPreferences = {
    ...preferences,
    timestamp: Date.now(),
  };
  
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
  
  // Only use Secure flag in production (HTTPS)
  const isProduction = process.env.NODE_ENV === 'production';
  const secureFlag = isProduction ? '; Secure' : '';
  
  const cookieString = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(fullPreferences))}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict${secureFlag}`;
  
  document.cookie = cookieString;
  
  // Debug logging only when explicitly enabled
  if (process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true') {
    console.log('[Consent] Saving preferences:', fullPreferences);
    console.log('[Consent] Cookie string:', cookieString);
    // Call getConsentPreferences after a small delay to ensure cookie is set
    setTimeout(() => {
      console.log('[Consent] Verification:', getConsentPreferences());
    }, 10);
  }
  
  // Dispatch event for other parts of the app
  window.dispatchEvent(new CustomEvent('consentChanged', { detail: fullPreferences }));
}

/**
 * Check if user has given consent
 */
export function hasConsent(): boolean {
  const preferences = getConsentPreferences();
  return preferences !== null;
}

/**
 * Check if specific consent type is granted
 */
export function hasConsentFor(type: keyof Omit<ConsentPreferences, 'timestamp'>): boolean {
  const preferences = getConsentPreferences();
  if (!preferences) return false;
  return preferences[type] === true;
}

/**
 * Accept all consent
 */
export function acceptAllConsent(): void {
  saveConsentPreferences({
    analytics: true,
    marketing: true,
    essential: true,
  });
}

/**
 * Reject all non-essential consent
 */
export function rejectNonEssentialConsent(): void {
  saveConsentPreferences({
    analytics: false,
    marketing: false,
    essential: true,
  });
}

/**
 * Update specific consent preference
 */
export function updateConsentPreference(
  type: keyof Omit<ConsentPreferences, 'timestamp'>,
  value: boolean
): void {
  const current = getConsentPreferences() || {
    analytics: false,
    marketing: false,
    essential: true,
  };
  
  saveConsentPreferences({
    ...current,
    [type]: value,
  });
}

/**
 * Clear all consent (for logout/data deletion)
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;
  
  document.cookie = `${CONSENT_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  
  window.dispatchEvent(new CustomEvent('consentCleared'));
}

/**
 * Get consent age in days
 */
export function getConsentAge(): number | null {
  const preferences = getConsentPreferences();
  if (!preferences) return null;
  
  const ageMs = Date.now() - preferences.timestamp;
  return Math.floor(ageMs / (1000 * 60 * 60 * 24));
}

/**
 * Check if consent needs renewal (e.g., after 1 year)
 */
export function needsConsentRenewal(maxAgeDays: number = 365): boolean {
  const age = getConsentAge();
  if (age === null) return true;
  return age > maxAgeDays;
}

/**
 * Hook to subscribe to consent changes
 */
export function onConsentChange(callback: (preferences: ConsentPreferences) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<ConsentPreferences>;
    callback(customEvent.detail);
  };
  
  window.addEventListener('consentChanged', handler);
  
  return () => {
    window.removeEventListener('consentChanged', handler);
  };
}